import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { catalogAPI } from '@matix/api-client';
import type { CatalogCategory, CatalogItem, SyncResponse } from '@matix/types';

/**
 * Catalog data hooks — smart-hybrid caching strategy.
 *
 * Strategy (matches the legacy Matix POS behavior, but better):
 *
 * 1. **At app boot** (`useCatalogSync` runs once when the cashier lands on
 *    the main POS screen): fire `GET /api/v1/catalog/sync?since=0` in the
 *    background. This pulls EVERY visible category and EVERY item in one
 *    request. The result is cached forever (`staleTime: Infinity`) under
 *    a dedicated query key so individual category/item lookups can read
 *    from it without refetching.
 *
 * 2. **When a category is selected** (`useItemsInCategory`): we first try
 *    to slice items out of the sync cache by `category_ids` membership.
 *    If the sync cache is populated (which it should be within ~1-2s of
 *    boot), the answer is instant — no network call.
 *
 * 3. **Fallback** (sync still loading or failed): we fall back to the
 *    per-category endpoint `GET /api/v1/catalog/categories/:id/items`.
 *    This guarantees the UI works even if sync is slow or errors out.
 *
 * 4. **Stale-while-revalidate**: TanStack Query's window-focus refetch is
 *    disabled globally (see main.tsx) so the cashier never sees a flicker.
 *    To pull updates, the cashier closes and re-opens the shift, which
 *    triggers a fresh sync. (Future: a "pull to refresh" gesture or a
 *    periodic background sync.)
 *
 * Net effect:
 *   - Boot: sync starts immediately, doesn't block the UI
 *   - First category click: usually instant (sync finished), worst case
 *     same speed as before (per-category fetch)
 *   - All subsequent clicks: instant
 *   - Memory cost: one full catalog snapshot per cashier session
 *     (typically <1 MB even for tenants with thousands of items)
 */

const CACHE_KEYS = {
  /** Full sync — ['catalog', 'sync']. Single source of truth. */
  sync: ['catalog', 'sync'] as const,

  /** Categories list (kept for backwards compat; reads from sync when available). */
  categories: ['catalog', 'categories'] as const,

  /** Per-category items fallback (only fired when sync hasn't loaded). */
  items: (categoryId: number) => ['catalog', 'category', categoryId, 'items'] as const,
} as const;

// ── Full-catalog sync ────────────────────────────────────────────────────

/**
 * Pull the entire catalog in one request. Call this once when the cashier
 * lands on the main POS screen (i.e. after shift open).
 *
 * Returns the full SyncResponse so callers can check `version` and watch
 * for changes. Most consumers should use `useCategories()` and
 * `useItemsInCategory()` instead, which read from this cache transparently.
 */
export function useCatalogSync(enabled = true) {
  return useQuery({
    queryKey: CACHE_KEYS.sync,
    queryFn: async () => {
      const r = await catalogAPI.sync(0);
      return r;
    },
    enabled,
    // The full sync is large (every item, incl. embedded images) so we do NOT
    // force a refetch on every mount — that would re-download tens of MB on
    // each tab switch. A finite staleTime means a fresh page load (in-memory
    // cache empty) pulls a current snapshot, while in-session remounts within
    // the window reuse it. Featured items have their own lightweight endpoint
    // (useFeaturedItems) so the landing page never waits on this. Pairs with
    // the NetworkFirst SW rule on /api/v1/catalog/sync (see vite.config.ts).
    staleTime: 5 * 60_000,
    gcTime: Infinity,
  });
}

/**
 * Imperatively trigger a sync if it hasn't run yet. Useful from screens
 * that want to ensure data is loaded without subscribing to the result.
 *
 * Returns a function — call it to start the sync.
 */
export function usePrefetchCatalog() {
  const qc = useQueryClient();
  return () =>
    qc.prefetchQuery({
      queryKey: CACHE_KEYS.sync,
      queryFn: () => catalogAPI.sync(0),
      staleTime: Infinity,
    });
}

// ── Category lookups (read from sync cache when available) ──────────────

/**
 * Collapse categories that arrive more than once under the same id.
 *
 * Branch visibility lives in `mstr_BranchesMenu`, which carries no unique
 * constraint, so a tenant can hold the same (subsidiary, branch, category) pair
 * twice — GL_WEB_pizzata does, for البيتزا. pos-api now asks that table with
 * EXISTS so it can no longer emit the category twice, but the till dedupes on
 * arrival as well, because a duplicate here is not a cosmetic double tile:
 * every category chip is keyed `key={c.id}`, and React's own warning for
 * non-unique keys is that children may be "duplicated and/or omitted". Each
 * drill-in-and-back re-reconciles that list and leaks another copy, which is
 * how two rows in the database became seven tiles on the screen.
 *
 * First occurrence wins, so the server's ordering is preserved.
 */
export function dedupeById(cats: CatalogCategory[]): CatalogCategory[] {
  const seen = new Set<number>();
  return cats.filter((c) => (seen.has(c.id) ? false : (seen.add(c.id), true)));
}

/**
 * A category whose parent is not in the branch's list is shown at the top.
 *
 * mstr_BranchesMenu links categories to a branch one by one, so a branch can
 * hold a sub-category without its parent. GL_WEB_ABH branch 62 holds only 226
 * (parent 194). The till draws the top level as `parent_id === null`, so 226
 * was never drawn and the cashier saw no categories at all. Such a category
 * is moved to the top here. A branch that holds the parent is unchanged.
 */
export function promoteOrphans(cats: CatalogCategory[]): CatalogCategory[] {
  const ids = new Set(cats.map((c) => c.id));
  return cats.map((c) =>
    c.parent_id != null && !ids.has(c.parent_id) ? { ...c, parent_id: null } : c,
  );
}

/** What the till draws: deduped, orphans at the top. */
export function normalizeCategories(cats: CatalogCategory[]): CatalogCategory[] {
  return promoteOrphans(dedupeById(cats));
}

/**
 * All visible categories. Reads from the sync cache if loaded; otherwise
 * falls back to the lightweight categories endpoint.
 */
export function useCategories() {
  const qc = useQueryClient();
  const syncData = qc.getQueryData<SyncResponse>(CACHE_KEYS.sync);

  return useQuery({
    queryKey: CACHE_KEYS.categories,
    queryFn: async () => {
      // Prefer sync cache.
      const cached = qc.getQueryData<SyncResponse>(CACHE_KEYS.sync);
      if (cached) return cached.changed_categories;
      // Fallback: pull just categories.
      const r = await catalogAPI.listCategories();
      return r.categories;
    },
    // If sync data is in the cache, we want to use it directly without an
    // initial-fetch flicker. initialData reads at mount time.
    initialData: syncData?.changed_categories,
    // Runs over initialData too, so the first paint is already normalized.
    select: normalizeCategories,
    staleTime: 60_000,
  });
}

/** Top-level categories (parent_id === null). Convenience selector. */
export function useTopLevelCategories() {
  const q = useCategories();
  return {
    ...q,
    data: q.data?.filter((c) => c.parent_id === null) ?? [],
  };
}

/** Child categories of a given parent. Convenience selector. */
export function useChildCategories(parentId: number | null) {
  const q = useCategories();
  return {
    ...q,
    data: parentId === null
      ? (q.data?.filter((c) => c.parent_id === null) ?? [])
      : (q.data?.filter((c) => c.parent_id === parentId) ?? []),
  };
}

// ── Items lookups (read from sync cache when available) ─────────────────

/**
 * Items inside a single category.
 *
 * If the sync cache is loaded, returns instantly by filtering its items
 * by `category_ids` membership. Otherwise falls back to the per-category
 * endpoint.
 *
 * Either way the result lands in the same query key so subsequent
 * activations are cached.
 */
/**
 * Sort items by the admin-set order WITHIN the given category
 * (pos_item_positions), then by name. Items with no position for that category
 * fall to the end. Mirrors the server-side ORDER BY so the cashier matches the
 * menu app even when it slices items out of the offline sync cache (which has
 * no inherent order).
 */
function sortByPosition(items: CatalogItem[], categoryId: number): CatalogItem[] {
  const posIn = (it: CatalogItem): number =>
    it.category_positions.find((cp) => cp.category_id === categoryId)?.position ??
    Number.MAX_SAFE_INTEGER;
  return [...items].sort((a, b) => {
    const pa = posIn(a);
    const pb = posIn(b);
    if (pa !== pb) return pa - pb;
    return (a.name ?? '').localeCompare(b.name ?? '');
  });
}

export function useItemsInCategory(categoryId: number | null) {
  const qc = useQueryClient();

  return useQuery({
    queryKey: categoryId === null ? ['catalog', 'noop'] : CACHE_KEYS.items(categoryId),
    queryFn: async () => {
      if (categoryId === null) return [];

      // Try sync cache first (instant, no network).
      const cached = qc.getQueryData<SyncResponse>(CACHE_KEYS.sync);
      if (cached) {
        return sortByPosition(
          cached.changed_items.filter((it) => it.category_ids.includes(categoryId)),
          categoryId,
        );
      }

      // Fallback: per-category endpoint (already server-ordered, but sort
      // again so both paths are identical).
      const r = await catalogAPI.listItemsInCategory(categoryId);
      return sortByPosition(r.items, categoryId);
    },
    // Read from sync cache synchronously at mount if available — no spinner.
    initialData: () => {
      if (categoryId === null) return undefined;
      const cached = qc.getQueryData<SyncResponse>(CACHE_KEYS.sync);
      if (!cached) return undefined;
      return sortByPosition(
        cached.changed_items.filter((it) => it.category_ids.includes(categoryId)),
        categoryId,
      );
    },
    enabled: categoryId !== null,
    staleTime: Infinity,
  });
}

/**
 * Featured items — the admin-flagged (`pos_featured`) highlights shown on the
 * cashier landing page, mirroring the legacy Matix POS "featured" grid.
 *
 * Backed by the dedicated `GET /api/v1/catalog/featured` endpoint — a small,
 * fast response (just the featured items) rather than filtering the full
 * catalog sync, which can be tens of MB once per-item images are embedded and
 * would otherwise stall the landing page until the whole catalog downloaded.
 *
 * `refetchOnMount: 'always'` keeps the grid fresh: flagging an item in admin
 * shows up the next time the landing mounts. The previous snapshot stays in
 * memory (`gcTime: Infinity`) so the refresh is a background update that never
 * blanks the grid. The server already orders by name.
 */
export function useFeaturedItems(): CatalogItem[] {
  const q = useQuery({
    queryKey: ['catalog', 'featured'] as const,
    queryFn: () => catalogAPI.featured(),
    staleTime: 60_000,
    gcTime: Infinity,
    refetchOnMount: 'always',
  });
  return q.data?.items ?? [];
}

/**
 * Every item in the catalog (from the sync cache). Used for global search —
 * matching by name / code / barcode across ALL categories rather than only
 * within the currently-browsed one. Subscribes to the sync query so it fills
 * in when a cold-cache sync resolves.
 */
export function useAllItems(): CatalogItem[] {
  const sync = useCatalogSync();
  return useMemo(() => sync.data?.changed_items ?? [], [sync.data]);
}

/**
 * Catalog items that carry option groups, by id — what the cart needs to
 * reopen the options window for a line. Reads the sync cache only (never
 * starts a download: the till already runs one), so it is empty until the
 * catalog has loaded.
 */
export function useModifierItemsById(): Map<number, CatalogItem> {
  const sync = useCatalogSync(false);
  return useMemo(() => {
    const m = new Map<number, CatalogItem>();
    for (const it of sync.data?.changed_items ?? []) {
      if (it.modifier_groups && it.modifier_groups.length > 0) m.set(it.id, it);
    }
    return m;
  }, [sync.data]);
}

// ── Lifecycle helper ─────────────────────────────────────────────────────

/**
 * Mount this once at the top of the main POS screen. Kicks off the sync
 * in the background — does NOT block the UI. The promise resolves when
 * data is ready, but most consumers won't await it; they'll just notice
 * subsequent category clicks become instant.
 */
export function useEagerCatalogLoad() {
  const prefetch = usePrefetchCatalog();
  useEffect(() => {
    void prefetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export type { CatalogCategory, CatalogItem, SyncResponse };
